// src/components/Input/Input.jsx
import React, { useId, useRef, useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import './Input.css';

export default function Input({
  className = '',
  error = false,
  errorText = '',
  onKeyDown,
  onChange,
  type = 'text',
  clearable = true,      // ⬅ allow disabling the clear "X" if needed
  revealable = true,     // ⬅ show reveal toggle for password fields
  ...props
}) {
  const id = useId();
  const inputRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const describedBy = error ? `${id}-error` : undefined;

  // Does this input currently have text?
  const isControlled = Object.prototype.hasOwnProperty.call(props, 'value');
  const hasContent = isControlled
    ? (props.value ?? '').toString().length > 0
    : !!inputRef.current?.value?.length;

  const isPasswordField = type === 'password';
  const showReveal = revealable && isPasswordField;
  const showClear  = clearable && hasContent && !props.disabled && !props.readOnly;

  const controlsCount = (showReveal ? 1 : 0) + (showClear ? 1 : 0);

  const handleClear = () => {
    if (onChange) {
      onChange({ target: { name: props.name, value: '' } });
    }
    if (!isControlled && inputRef.current) {
      inputRef.current.value = '';
    }
    inputRef.current?.focus();
  };

  const actualType = showReveal ? (revealed ? 'text' : 'password') : type;

  return (
    <div className={`input-group ${className} ${error ? 'has-error' : ''}`}>
      <input
        ref={inputRef}
        className="input-field"
        type={actualType}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={describedBy}
        // dynamic right padding for 0/1/2 icons
        style={{
          // 14px default right pad when no icons
          // 40px when one icon, ~76px when two
          ['--input-right-pad']: controlsCount === 2 ? '76px'
                                 : controlsCount === 1 ? '40px'
                                 : '14px'
        }}
        onKeyDown={(e) => {
          // ESC clears when there's content
          if (e.key === 'Escape' && hasContent) {
            e.preventDefault();
            handleClear();
          }
          onKeyDown?.(e);
        }}
        onChange={onChange}
        {...props}
      />

      {(showReveal || showClear) && (
        <div className="input-controls" aria-hidden="true">
          {showReveal && (
            <button
              type="button"
              className="input-btn input-reveal"
              onClick={() => setRevealed(v => !v)}
              tabIndex={0}
              aria-label={revealed ? 'Hide password' : 'Show password'}
              title={revealed ? 'Hide password' : 'Show password'}
            >
              {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}

          {showClear && (
            <button
              type="button"
              className="input-btn input-clear"
              aria-label="Clear"
              onClick={handleClear}
              tabIndex={0}
              title="Clear"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {errorText && (
        <div id={describedBy} className="input-error" role="alert">
          {errorText}
        </div>
      )}
    </div>
  );
}
